"use client";

import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { FileItemIcon } from "@/components/files/FileItemIcon";
import { FileTypeBadge } from "@/components/files/FileTypeBadge";
import type { FileEntryItem } from "@/lib/files/server";

function isFileAvailable(file: FileEntryItem): boolean {
  return file.isAvailable === true && Boolean(file.fileUrl);
}

export function FileList({
  files,
  isAr,
}: {
  files: FileEntryItem[];
  isAr: boolean;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => f.title.toLowerCase().includes(q));
  }, [files, query]);

  const availableCount = filtered.filter(isFileAvailable).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          {isAr
            ? `${availableCount} ملف`
            : `${availableCount} file${availableCount === 1 ? "" : "s"}`}
        </p>
        <label className="sr-only" htmlFor="files-search">
          {isAr ? "ابحث بالاسم" : "Search by name"}
        </label>
        <input
          id="files-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={isAr ? "ابحث بالاسم..." : "Search by name..."}
          className="w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm shadow-sm outline-none ring-[var(--association-theme-color-1)] transition focus:ring-2 sm:max-w-sm"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center text-muted-foreground">
          {isAr ? "لا توجد ملفات." : "No files found."}
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((file) => {
            const available = isFileAvailable(file);
            return (
              <li key={file.id}>
                <article
                  className={`group flex h-full flex-col overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm ${
                    available
                      ? "transition hover:border-[color-mix(in_srgb,var(--association-theme-color-1)_45%,transparent)] hover:shadow-md"
                      : "opacity-80"
                  }`}
                >
                  <div
                    className={`flex aspect-[16/10] items-center justify-center ${
                      available
                        ? "bg-[color-mix(in_srgb,var(--association-theme-color-1)_8%,transparent)] text-[var(--association-theme-color-1)]"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    <FileItemIcon
                      className={`h-24 w-24 ${
                        available
                          ? "transition duration-500 group-hover:scale-[1.06]"
                          : ""
                      }`}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-5">
                    {available && file.fileType !== "placeholder" ? (
                      <div>
                        <FileTypeBadge type={file.fileType} />
                      </div>
                    ) : null}
                    <h2
                      className={`text-base font-semibold leading-snug tracking-tight md:text-lg ${
                        available ? "" : "text-muted-foreground"
                      }`}
                    >
                      {file.title}
                    </h2>
                    <div className="mt-auto pt-2">
                      {available ? (
                        <a
                          href={file.fileUrl!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--association-theme-color-1)] px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                        >
                          {isAr ? "عرض الملف" : "View file"}
                          <ArrowRight
                            className="h-4 w-4 rtl:rotate-180"
                            aria-hidden
                          />
                        </a>
                      ) : (
                        <span className="inline-flex w-full items-center justify-center rounded-xl bg-muted px-4 py-2.5 text-sm font-medium text-muted-foreground">
                          {isAr ? "غير متوفر حالياً" : "Not available yet"}
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
